/**
 * Volume envelopes shared by the renderer normalizers, the Remotion compositions
 * and the (vendored) frontend editor. Must stay free of Node imports.
 *
 * A point is `{t, v}` with `t` in SECONDS on the CLIP timeline (never source time)
 * and `v` a 0..1 gain multiplier. Values are linearly interpolated between points;
 * before the first point the first `v` holds, after the last point the last `v` holds.
 */
import {DEFAULT_FPS} from './timeline';

export interface VolumePoint {
  t: number;
  v: number;
}

const finiteNumber = (value: unknown): number | undefined => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : undefined;
};

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

/**
 * Accepts the raw metadata value (array, JSON string, or garbage), drops every entry
 * that cannot be read as a `{t, v}` pair, clamps `t >= 0` and `v` into 0..1 and returns
 * the survivors sorted by `t`. Never throws — a malformed envelope degrades to `[]`,
 * which callers treat as "no envelope" (constant volume).
 */
export const sortAndValidatePoints = (raw: unknown): VolumePoint[] => {
  const list = toArray(raw);
  if (list.length === 0) {
    return [];
  }

  const points: VolumePoint[] = [];
  for (const entry of list) {
    if (!entry || typeof entry !== 'object') {
      continue;
    }
    const record = entry as Record<string, unknown>;
    const t = finiteNumber(record.t ?? record.time ?? record.seconds);
    const v = finiteNumber(record.v ?? record.volume ?? record.value);
    if (t === undefined || v === undefined || t < 0) {
      continue;
    }
    points.push({t, v: clamp01(v)});
  }

  return points.sort((left, right) => left.t - right.t);
};

const toArray = (raw: unknown): unknown[] => {
  if (Array.isArray(raw)) {
    return raw;
  }
  if (typeof raw !== 'string' || raw.trim() === '') {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/**
 * Gain at `frame` (on the same timeline the points are expressed in).
 * An empty envelope means "no envelope" and resolves to full gain.
 */
export const resolveVolume = (points: VolumePoint[], frame: number, fps: number): number => {
  if (!points || points.length === 0) {
    return 1;
  }

  const safeFps = Number.isFinite(fps) && fps > 0 ? fps : DEFAULT_FPS;
  const time = (Number.isFinite(frame) ? frame : 0) / safeFps;

  const first = points[0];
  if (time <= first.t) {
    return first.v;
  }
  const last = points[points.length - 1];
  if (time >= last.t) {
    return last.v;
  }

  for (let index = 1; index < points.length; index += 1) {
    const previous = points[index - 1];
    const current = points[index];
    if (time <= current.t) {
      const span = current.t - previous.t;
      if (span <= 0) {
        // Duplicate timestamps: the later point wins, matching last-writer-wins elsewhere.
        return current.v;
      }
      const progress = (time - previous.t) / span;
      return previous.v + (current.v - previous.v) * progress;
    }
  }

  return last.v;
};
